import { HttpClient } from "@angular/common/http";
import { inject, Inject, Injectable } from "@angular/core";
import { EnvConfig, Environment } from "@doc/core";
import { BehaviorSubject, Observable, map, tap } from "rxjs";
import { ApiUrls } from "../backoffice-urls";

interface User {
  id: number;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  token?: string;
  email: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private isFullAccessSubject = new BehaviorSubject<boolean>(false);
  private permissionsSubject = new BehaviorSubject<number[]>([]);
  private delegation = new BehaviorSubject<any[]>([]);
  permissions$ = this.permissionsSubject.asObservable();
  private http = inject(HttpClient);
  private apiUrl: string;
  private baseUrl: string;

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  constructor(@Inject(EnvConfig) private config: Environment) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("loginObject") || "{}")
    );
    this.currentUser = this.currentUserSubject.asObservable();
    console.log("Current User from localStorage:", this.currentUserSubject.getValue().token);
  }

  login(payload: any) {
    const link = this.apiUrl + ApiUrls.login;
    return this.http.post<any>(link, payload).pipe(
      tap((response: any) => {
        if (response.status) {
          if (response.data.isFullAccess) {
            this.isFullAccessSubject.next(response.data.isFullAccess);
          } else {
            this.permissionsSubject.next(response.data.permissions);
          }
          if (response?.data?.delegation?.totalCount > 0) {
            this.delegation.next(response?.data?.delegation?.items);
          }
        }
      })
    );
  }

  loginsso(payload: any) {
    const link = this.apiUrl + ApiUrls.loginsso;

    return this.http.post<any>(link, { azureToken: payload }).pipe(
      tap((response: any) => {
        if (response.status) {
          localStorage.setItem("customToken", response.data.Token); // ✅ Store custom JWT
          if (response?.data?.isFullAccess) {
            this.isFullAccessSubject.next(response.data.isFullAccess);
          } else {
            this.permissionsSubject.next(response.data.permissions);
          }
          if (response?.data?.delegation?.totalCount > 0) {
            this.delegation.next(response?.data?.delegation?.items);
          }
        }
      })
    );
  }

  isAuthenticated() {
    return this.currentUserSubject.getValue().token;
  }

  getDelegation(): any[] {
    return this.delegation.getValue();
  }

  getPermissions(): number[] {
    return this.permissionsSubject.getValue();
  }

  isFullAccess(): boolean {
    return this.isFullAccessSubject.getValue();
  }

  attemptLogin(email: string, password: string) {
    return this.login({ email, password }).pipe(
      map((user) => {
        // login successful if there's a jwt token in the response
        if (user && user.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.setItem("currentUser", JSON.stringify(user));
          this.currentUserSubject.next(user);
        }
        return user;
      })
    );
  }

  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem("currentUser");
    this.currentUserSubject.next(JSON.parse("{}"));
  }

  forgotPassword(email: string) {
    const link = this.apiUrl + ApiUrls.forgotPassword;
    return this.http.post<any>(link, { email });
  }

  passwordOtp(body: any) {
    const link = this.apiUrl + ApiUrls.passwordOtp;
    return this.http.post<any>(link, body);
  }

  changePassword(body: any) {
    const link = this.apiUrl + ApiUrls.resetPassword;
    return this.http.post<any>(link, body);
  }

  invitedUserSignup(body: any) {
    const link = this.apiUrl + ApiUrls.signupUser;
    return this.http.post<any>(link, body);
  }
}
